"use client"

import { useMemo, useState } from "react"
import { openStockChart } from "@/lib/utils"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { ColumnFilter } from "@/components/ui/ColumnFilter"
import { AddToBacktestButton } from "@/components/watchlist/AddToBacktestButton"
import { ThemeBadges } from "@/components/shared/ThemeBadges"
import type { Stock } from "@/types/stock"

interface StockTableProps {
  stocks: Stock[]
  isLoading: boolean
  watchlistCodes?: Set<string>
}

const NULL_LABEL = "(없음)"

function AuditBadge({ auditInfo }: { auditInfo: string | null }) {
  if (auditInfo === "정상")
    return (
      <Badge
        variant="outline"
        className="border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
      >
        정상
      </Badge>
    )
  if (auditInfo === "투자주의")
    return (
      <Badge
        variant="outline"
        className="border-yellow-500/40 bg-yellow-500/10 text-yellow-400"
      >
        투자주의
      </Badge>
    )
  if (auditInfo === "거래정지")
    return (
      <Badge
        variant="outline"
        className="border-red-500/40 bg-red-500/10 text-red-400"
      >
        거래정지
      </Badge>
    )
  return (
    <Badge
      variant="outline"
      className="border-zinc-500/40 bg-zinc-500/10 text-zinc-400"
    >
      {auditInfo ?? "-"}
    </Badge>
  )
}

function StatusBadges({ stock }: { stock: Stock }) {
  const badges = []
  const w = stock.orderWarning ?? ""

  if (w.includes("관리"))
    badges.push(
      <Badge
        key="managed"
        variant="outline"
        className="border-yellow-500/40 bg-yellow-500/10 text-yellow-400"
      >
        관리
      </Badge>
    )
  if (w.includes("위험"))
    badges.push(
      <Badge
        key="danger"
        variant="outline"
        className="border-red-500/40 bg-red-500/10 text-red-400"
      >
        위험
      </Badge>
    )
  if (w.includes("경고"))
    badges.push(
      <Badge
        key="warning"
        variant="outline"
        className="border-orange-500/40 bg-orange-500/10 text-orange-400"
      >
        경고
      </Badge>
    )
  if (w.includes("주의"))
    badges.push(
      <Badge
        key="caution"
        variant="outline"
        className="border-orange-500/40 bg-orange-500/10 text-orange-400"
      >
        주의
      </Badge>
    )
  if (!stock.isActive)
    badges.push(
      <Badge
        key="inactive"
        variant="outline"
        className="border-zinc-500/40 bg-zinc-500/10 text-zinc-400"
      >
        비활성
      </Badge>
    )

  return badges.length > 0 ? <>{badges}</> : null
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i} className="border-zinc-800">
          {Array.from({ length: 9 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton className="h-4 w-full bg-zinc-800" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  )
}

function MobileSkeletonCards() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="flex flex-col gap-2 px-4 py-3">
          <Skeleton className="h-4 w-2/3 bg-zinc-800" />
          <Skeleton className="h-3 w-1/3 bg-zinc-800" />
        </div>
      ))}
    </>
  )
}

function MarketBadge({ market }: { market: Stock["market"] }) {
  return (
    <Badge
      variant="outline"
      className={
        market === "KOSPI"
          ? "border-blue-500/40 bg-blue-500/10 text-blue-400"
          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
      }
    >
      {market}
    </Badge>
  )
}

type ColKey = "market" | "sector" | "auditInfo"

export function StockTable({ stocks, isLoading, watchlistCodes }: StockTableProps) {
  const [colFilters, setColFilters] = useState<Record<ColKey, Set<string>>>({
    market: new Set(),
    sector: new Set(),
    auditInfo: new Set(),
  })

  const updateFilter = (key: ColKey, val: Set<string>) =>
    setColFilters((prev) => ({ ...prev, [key]: val }))

  const marketValues = useMemo(
    () => [...new Set(stocks.map((s) => s.market))].sort(),
    [stocks]
  )
  const sectorValues = useMemo(
    () => [...new Set(stocks.map((s) => s.sector ?? NULL_LABEL))].sort(),
    [stocks]
  )
  const auditValues = useMemo(
    () => [...new Set(stocks.map((s) => s.auditInfo ?? NULL_LABEL))].sort(),
    [stocks]
  )

  const displayed = useMemo(() => {
    return stocks.filter((s) => {
      if (colFilters.market.size > 0 && !colFilters.market.has(s.market)) return false
      if (colFilters.sector.size > 0 && !colFilters.sector.has(s.sector ?? NULL_LABEL)) return false
      if (colFilters.auditInfo.size > 0 && !colFilters.auditInfo.has(s.auditInfo ?? NULL_LABEL)) return false
      return true
    })
  }, [stocks, colFilters])

  return (
    <>
      {/* md 이상: 기존 테이블 */}
      <div className="hidden md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400 w-24">종목코드</TableHead>
              <TableHead className="text-zinc-400">종목명</TableHead>
              <TableHead className="text-zinc-400 w-24">
                <div className="flex items-center gap-1">
                  시장
                  <ColumnFilter
                    values={marketValues}
                    selected={colFilters.market}
                    onChange={(v) => updateFilter("market", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400">
                <div className="flex items-center gap-1">
                  섹터
                  <ColumnFilter
                    values={sectorValues}
                    selected={colFilters.sector}
                    onChange={(v) => updateFilter("sector", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="text-zinc-400">업종</TableHead>
              <TableHead className="text-zinc-400">테마</TableHead>
              <TableHead className="text-zinc-400 w-32">유동주식수</TableHead>
              <TableHead className="text-zinc-400 w-40">
                <div className="flex items-center gap-1">
                  상태
                  <ColumnFilter
                    values={auditValues}
                    selected={colFilters.auditInfo}
                    onChange={(v) => updateFilter("auditInfo", v)}
                  />
                </div>
              </TableHead>
              <TableHead className="w-10 text-zinc-400" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : displayed.length === 0 ? (
              <TableRow className="border-zinc-800">
                <TableCell
                  colSpan={9}
                  className="py-12 text-center text-zinc-500"
                >
                  검색 결과가 없습니다.
                </TableCell>
              </TableRow>
            ) : (
              displayed.map((stock) => (
                <TableRow
                  key={stock.stockCode}
                  className="border-zinc-800 text-zinc-200 cursor-pointer hover:bg-zinc-800/60"
                  onClick={() => openStockChart(stock.stockCode)}
                >
                  <TableCell className="font-mono text-zinc-400">
                    {stock.stockCode}
                  </TableCell>
                  <TableCell className="font-medium">{stock.stockName}</TableCell>
                  <TableCell>
                    <MarketBadge market={stock.market} />
                  </TableCell>
                  <TableCell className="text-zinc-300">
                    {stock.sector ?? "-"}
                  </TableCell>
                  <TableCell className="text-zinc-300">
                    {stock.industry ?? "-"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <ThemeBadges themes={stock.themes} />
                  </TableCell>
                  <TableCell className="text-zinc-300 font-mono text-sm">
                    {stock.floatShares != null
                      ? stock.floatShares.toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <AuditBadge auditInfo={stock.auditInfo} />
                      <StatusBadges stock={stock} />
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <AddToBacktestButton
                      stockCode={stock.stockCode}
                      inWatchlist={watchlistCodes?.has(stock.stockCode) ?? false}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* md 미만: 카드형 리스트 */}
      <div className="divide-y divide-zinc-800 md:hidden">
        {isLoading ? (
          <MobileSkeletonCards />
        ) : displayed.length === 0 ? (
          <div className="py-12 text-center text-zinc-500">검색 결과가 없습니다.</div>
        ) : (
          displayed.map((stock) => (
            <div
              key={stock.stockCode}
              className="flex min-h-[44px] cursor-pointer flex-col gap-1.5 px-4 py-3 text-zinc-200 active:opacity-80"
              onClick={() => openStockChart(stock.stockCode)}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="truncate font-medium">{stock.stockName}</span>
                <span className="shrink-0 font-mono text-xs text-zinc-500">{stock.stockCode}</span>
              </div>
              <div className="flex items-center gap-2">
                <MarketBadge market={stock.market} />
                <span className="truncate text-xs text-zinc-400">{stock.sector ?? "-"}</span>
                {stock.industry && (
                  <span className="truncate text-xs text-zinc-600">· {stock.industry}</span>
                )}
              </div>
              {stock.themes.length > 0 && (
                <div onClick={(e) => e.stopPropagation()}>
                  <ThemeBadges themes={stock.themes} />
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-zinc-400">
                  {stock.floatShares != null ? stock.floatShares.toLocaleString() : "-"}
                </span>
                <div className="flex items-center gap-1">
                  <AuditBadge auditInfo={stock.auditInfo} />
                  <StatusBadges stock={stock} />
                  <AddToBacktestButton
                    stockCode={stock.stockCode}
                    inWatchlist={watchlistCodes?.has(stock.stockCode) ?? false}
                  />
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </>
  )
}
